// IndexedDB backend (Phase 1) — now with at-rest encryption.
//
// Schema (DB v2):
//   photos store    keyPath 'file'    index 'by_date'(date)
//     record: { file, date, profile, takenAt, savedAt,
//               scaleObject, hasScaleReference, lighting, tags,
//               encrypted?: true, iv?: Uint8Array, cipher?: Blob,
//               blob?: Blob              // legacy unencrypted records
//             }
//   journals store  keyPath 'date'
//     record: { date, savedAt,
//               encrypted?: true, iv?: Uint8Array, cipher?: Uint8Array,
//               journal?: object         // legacy unencrypted records
//             }
//   config store    keyPath 'key'
//     record: { key, value }            // PIN salt, verifier, parent name, autoLockMs, etc.
//
// Encryption is transparent to the rest of the app: callers still hand in a
// Blob / object and get back the same shape. The backend handles encrypt on
// save and decrypt on load, given a session key that the session module sets
// via setSessionKey().
//
// Records written before PIN setup have no `encrypted` flag and still contain
// the plain blob / object. `migrateAllToEncrypted()` rewrites them once a key
// is set — used during first-PIN onboarding.

import { encryptBlob, decryptBlob, encryptJSON, decryptJSON } from '../crypto/crypto.js';

const DB_NAME = 'maxhealth';
const DB_VERSION = 2;

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = req.result;
      // v1 → v2: add the config store, leave photos/journals as-is.
      if (!db.objectStoreNames.contains('photos')) {
        const photos = db.createObjectStore('photos', { keyPath: 'file' });
        photos.createIndex('by_date', 'date', { unique: false });
      }
      if (!db.objectStoreNames.contains('journals')) {
        db.createObjectStore('journals', { keyPath: 'date' });
      }
      if (!db.objectStoreNames.contains('config')) {
        db.createObjectStore('config', { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function store(db, name, mode = 'readonly') {
  return db.transaction(name, mode).objectStore(name);
}

function asPromise(req) {
  return new Promise((res, rej) => {
    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
  });
}

export function createIndexedDBBackend() {
  let dbPromise;
  const db = () => (dbPromise ??= openDB());

  // The in-memory session key, set via setSessionKey() on unlock and cleared
  // via clearSessionKey() on lock / app background.
  let sessionKey = null;

  function requireKey() {
    if (!sessionKey) throw new Error('Locked: session key not set');
    return sessionKey;
  }

  return {
    // ---- Session key management ----

    setSessionKey(key) { sessionKey = key; },
    clearSessionKey()  { sessionKey = null; },
    hasSessionKey()    { return !!sessionKey; },

    // ---- Photos ----

    async savePhoto(blob, meta) {
      if (!meta?.file || !meta?.date || !meta?.profile) {
        throw new Error('savePhoto: meta requires { file, date, profile }');
      }
      const key = requireKey();
      const { iv, cipher } = await encryptBlob(key, blob);
      const record = {
        file: meta.file,
        date: meta.date,
        profile: meta.profile,
        scaleObject: meta.scaleObject ?? null,
        hasScaleReference: meta.hasScaleReference ?? false,
        lighting: meta.lighting ?? null,
        tags: meta.tags ?? [],
        takenAt: meta.takenAt ?? new Date().toISOString(),
        savedAt: new Date().toISOString(),
        encrypted: true,
        iv,
        cipher,
      };
      const d = await db();
      await asPromise(store(d, 'photos', 'readwrite').add(record));
      return { file: meta.file };
    },

    async loadPhoto(file) {
      const d = await db();
      const r = await asPromise(store(d, 'photos').get(file));
      if (!r) return null;
      if (r.encrypted) {
        const key = requireKey();
        return await decryptBlob(key, { iv: r.iv, cipher: r.cipher }, 'image/jpeg');
      }
      // Legacy unencrypted record (pre-security-pass).
      return r.blob ?? null;
    },

    async listPhotos(date) {
      // Metadata only — no decryption needed.
      const d = await db();
      const idx = store(d, 'photos').index('by_date');
      const rows = await asPromise(idx.getAll(IDBKeyRange.only(date)));
      return rows.map(({ blob, iv, cipher, ...rest }) => rest);
    },

    async deletePhoto(file) {
      const d = await db();
      await asPromise(store(d, 'photos', 'readwrite').delete(file));
    },

    // ---- Journals ----

    async saveJournal(date, journal) {
      const key = requireKey();
      const { iv, cipher } = await encryptJSON(key, journal);
      const d = await db();
      await asPromise(
        store(d, 'journals', 'readwrite').put({
          date,
          savedAt: new Date().toISOString(),
          encrypted: true,
          iv,
          cipher,
        })
      );
    },

    async loadJournal(date) {
      const d = await db();
      const r = await asPromise(store(d, 'journals').get(date));
      if (!r) return null;
      if (r.encrypted) {
        const key = requireKey();
        return await decryptJSON(key, { iv: r.iv, cipher: r.cipher });
      }
      return r.journal ?? null;
    },

    async listEntryDates({ from, to } = {}) {
      const d = await db();
      const [journalDates, photoDates] = await Promise.all([
        asPromise(store(d, 'journals').getAllKeys()),
        asPromise(store(d, 'photos').index('by_date').getAllKeys()),
      ]);
      const all = new Set([...journalDates, ...photoDates]);
      let arr = Array.from(all).sort();
      if (from) arr = arr.filter((x) => x >= from);
      if (to)   arr = arr.filter((x) => x <= to);
      return arr;
    },

    // ---- Config (PIN salt/verifier, parent name, settings) ----

    async getConfig(key) {
      const d = await db();
      const r = await asPromise(store(d, 'config').get(key));
      return r ? r.value : null;
    },

    async setConfig(key, value) {
      const d = await db();
      await asPromise(store(d, 'config', 'readwrite').put({ key, value }));
    },

    async deleteConfig(key) {
      const d = await db();
      await asPromise(store(d, 'config', 'readwrite').delete(key));
    },

    // ---- Migration & wipe ----

    /**
     * Re-encrypt any records that pre-date PIN setup (no `encrypted` flag).
     * Idempotent. Called once after first PIN setup.
     */
    async migrateAllToEncrypted() {
      const key = requireKey();
      const d = await db();

      // Photos
      const photoRows = await asPromise(store(d, 'photos').getAll());
      const photoTx = d.transaction('photos', 'readwrite').objectStore('photos');
      for (const r of photoRows) {
        if (r.encrypted) continue;
        if (!r.blob) continue; // nothing to migrate
        const { iv, cipher } = await encryptBlob(key, r.blob);
        const next = { ...r, iv, cipher, encrypted: true };
        delete next.blob;
        await asPromise(photoTx.put(next));
      }

      // Journals
      const journalRows = await asPromise(store(d, 'journals').getAll());
      const jtx = d.transaction('journals', 'readwrite').objectStore('journals');
      for (const r of journalRows) {
        if (r.encrypted) continue;
        if (!r.journal) continue;
        const { iv, cipher } = await encryptJSON(key, r.journal);
        const next = { date: r.date, savedAt: r.savedAt, encrypted: true, iv, cipher };
        await asPromise(jtx.put(next));
      }
    },

    /**
     * Destroy the entire database. Used by the "wipe all" panic button and on
     * "forgot PIN" reset. Returns when the DB is deleted.
     */
    async wipeAll() {
      // Close any open connection first.
      if (dbPromise) {
        const d = await dbPromise;
        d.close();
        dbPromise = null;
      }
      sessionKey = null;
      await new Promise((res, rej) => {
        const req = indexedDB.deleteDatabase(DB_NAME);
        req.onsuccess = () => res();
        req.onerror = () => rej(req.error);
        req.onblocked = () => rej(new Error('Database delete blocked — close other tabs and retry.'));
      });
    },
  };
}
